import { useEffect, useMemo, useState } from "react";
import CoursesApi from "../../../../api/coursesApi";
import { Project } from "../../../../types/Company";
import { Course } from "../../../../types/Courses";
import { getTranslation } from "../../../../utils/getTranslation";
import s from "../ProjectFormPage.module.css";
import { ProjectFormController } from "../useProjectFormController";

type CoursesStepProps = {
  controller: ProjectFormController;
  project: Project | null;
  token: string | null;
};

const getCourseTitle = (course: Course) => {
  if (Array.isArray(course.title)) {
    return getTranslation(course.title, "en");
  }

  return course.title || "Untitled Course";
};

const getCourseDescription = (course: Course) =>
  course.briefDescription || course.description || "";

const getCourseAuthor = (course: Course) => {
  const name = getTranslation(course.createdBy?.name, "en");
  const surname = getTranslation(course.createdBy?.surname, "en");

  return [name, surname].filter(Boolean).join(" ");
};

const getCourseStructureText = (course: Course) => {
  const chaptersAmount = course.chapters?.length ?? 0;
  const lecturesAmount =
    course.chapters?.reduce(
      (total, chapter) => total + (chapter.lectures?.length ?? 0),
      0,
    ) ?? 0;

  if (!chaptersAmount && !lecturesAmount) return "";

  return `${chaptersAmount} chapters / ${lecturesAmount} lectures`;
};

const getCoursePrice = (course: Course) => {
  if (course.cost === undefined || course.cost === null) return "";
  return `${course.cost / 100} USD`;
};

const CoursesStep = ({ controller, project, token }: CoursesStepProps) => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [projectCourses, setProjectCourses] = useState<Course[]>([]);
  const [courseImages, setCourseImages] = useState<Record<string, string>>({});
  const [projectCoursesLoading, setProjectCoursesLoading] = useState(false);
  const [deletingCourseIds, setDeletingCourseIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [projectCoursesErrorMessage, setProjectCoursesErrorMessage] =
    useState("");
  const projectId = project?._id;

  useEffect(() => {
    if (!token) {
      setErrorMessage("Authentication token is missing. Please sign in again.");
      return;
    }

    let ignore = false;

    const loadCourses = async () => {
      setLoading(true);
      setErrorMessage("");

      try {
        const response = await CoursesApi.getFilteredCourses(token, {
          page: 1,
          limit: 100,
          isAvailableToCompanies: true,
        });

        if (ignore) return;

        if (response.status) {
          setCourses(response.courses ?? []);
        } else {
          setErrorMessage(
            response.message || "Failed to load available Courses.",
          );
        }
      } catch (error) {
        if (ignore) return;
        console.error("Error loading available Courses:", error);
        setErrorMessage("Something went wrong while loading Courses.");
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    loadCourses();

    return () => {
      ignore = true;
    };
  }, [token]);

  useEffect(() => {
    if (!token || !projectId) {
      setProjectCourses([]);
      setProjectCoursesErrorMessage("");
      return;
    }

    let ignore = false;

    const loadProjectCourses = async () => {
      setProjectCoursesLoading(true);
      setProjectCoursesErrorMessage("");

      try {
        const response = await CoursesApi.getFilteredCourses(token, {
          page: 1,
          limit: 100,
          project: projectId,
        });

        if (ignore) return;

        if (response.status) {
          setProjectCourses(response.courses ?? []);
        } else {
          setProjectCoursesErrorMessage(
            response.message || "Failed to load Project Courses.",
          );
        }
      } catch (error) {
        if (ignore) return;
        console.error("Error loading Project Courses:", error);
        setProjectCoursesErrorMessage(
          "Something went wrong while loading Project Courses.",
        );
      } finally {
        if (!ignore) {
          setProjectCoursesLoading(false);
        }
      }
    };

    loadProjectCourses();

    return () => {
      ignore = true;
    };
  }, [projectId, token]);

  useEffect(() => {
    const visibleCourses = [...courses, ...projectCourses];

    if (!token || !visibleCourses.length) {
      setCourseImages({});
      return;
    }

    let ignore = false;

    const loadCourseImages = async () => {
      const coursesWithImages = visibleCourses.filter(
        (course) => course.coverImage,
      );
      if (!coursesWithImages.length) {
        setCourseImages({});
        return;
      }

      try {
        const responses = await Promise.all(
          coursesWithImages.map(async (course) => {
            const response = await CoursesApi.getCourseFileSignedUrl(
              token,
              course.coverImage!,
            );

            return {
              courseId: course._id,
              url: response.status ? response.url : undefined,
            };
          }),
        );

        if (ignore) return;

        setCourseImages(
          responses.reduce<Record<string, string>>((acc, item) => {
            if (item.url) {
              acc[item.courseId] = item.url;
            }

            return acc;
          }, {}),
        );
      } catch (error) {
        if (ignore) return;
        console.error("Error loading Course images:", error);
        setCourseImages({});
      }
    };

    loadCourseImages();

    return () => {
      ignore = true;
    };
  }, [courses, projectCourses, token]);

  const selectedCourseIds = controller.selectedCourseIds;
  const projectSourceCourseIds = useMemo(
    () =>
      new Set(
        projectCourses
          .map((course) => course.sourceCourse)
          .filter(Boolean) as string[],
      ),
    [projectCourses],
  );
  const availableCourses = useMemo(
    () => courses.filter((course) => !projectSourceCourseIds.has(course._id)),
    [courses, projectSourceCourseIds],
  );
  const selectedCourses = useMemo(
    () =>
      availableCourses.filter((course) =>
        selectedCourseIds.includes(course._id),
      ),
    [availableCourses, selectedCourseIds],
  );
  const selectedCount = selectedCourses.length;
  const setSelectedCourseIds = controller.setSelectedCourseIds;

  useEffect(() => {
    setSelectedCourseIds((prev) => {
      const next = prev.filter(
        (courseId) => !projectSourceCourseIds.has(courseId),
      );

      return next.length === prev.length ? prev : next;
    });
  }, [projectSourceCourseIds, setSelectedCourseIds]);

  const toggleCourse = (courseId: string) => {
    setSelectedCourseIds((prev) =>
      prev.includes(courseId)
        ? prev.filter((item) => item !== courseId)
        : [...prev, courseId],
    );
  };

  const deleteProjectCourse = async (courseId: string) => {
    if (!token) {
      setProjectCoursesErrorMessage(
        "Authentication token is missing. Please sign in again.",
      );
      return;
    }

    setDeletingCourseIds((prev) => [...prev, courseId]);
    setProjectCoursesErrorMessage("");

    try {
      const response = await CoursesApi.delete(token, courseId);

      if (response.status) {
        setProjectCourses((prev) =>
          prev.filter((course) => course._id !== courseId),
        );
        return;
      }

      setProjectCoursesErrorMessage(
        response.message || "Failed to delete the Course from this Project.",
      );
    } catch (error) {
      console.error("Error deleting Project Course:", error);
      setProjectCoursesErrorMessage(
        "Something went wrong while deleting the Course from this Project.",
      );
    } finally {
      setDeletingCourseIds((prev) => prev.filter((item) => item !== courseId));
    }
  };

  return (
    <div className={s.stepPanel}>
      <section className={s.section}>
        <div>
          <h2 className={s.sectionTitle}>Courses</h2>
          <p className={s.sectionText}>
            Add company-available Courses to this Project.
          </p>
        </div>

        {loading && <div className={s.emptyState}>Loading Courses...</div>}

        {errorMessage && (
          <div className={s.formAlert} role="alert">
            {errorMessage}
          </div>
        )}

        {!loading && !errorMessage && availableCourses.length === 0 && (
          <div className={s.emptyState}>
            No company-available Courses found.
          </div>
        )}

        {!loading && !errorMessage && availableCourses.length > 0 && (
          <>
            <div className={s.courseSummary}>
              <span className={s.value}>{selectedCount}</span>
              <span className={s.label}>
                {selectedCount === 1 ? "Course selected" : "Courses selected"}
              </span>
            </div>

            <div className={s.courseGrid}>
              {availableCourses.map((course) => {
                const selected = selectedCourseIds.includes(course._id);
                const title = getCourseTitle(course);
                const description = getCourseDescription(course);
                const author = getCourseAuthor(course);
                const structureText = getCourseStructureText(course);
                const levels = course.levels?.join(", ");
                const tags = course.tags?.map((tag) => tag.title).join(", ");
                const imageUrl = courseImages[course._id];
                const price = getCoursePrice(course);

                return (
                  <button
                    key={course._id}
                    type="button"
                    className={`${s.courseCard} ${
                      selected ? s.selectedCourseCard : ""
                    }`}
                    onClick={() => toggleCourse(course._id)}
                    aria-pressed={selected}
                  >
                    <span className={s.courseCardHeader}>
                      <span className={s.courseImageBlock}>
                        {imageUrl ? (
                          <img
                            className={s.courseImage}
                            src={imageUrl}
                            alt={`${title} logo`}
                          />
                        ) : (
                          <span className={s.courseImageFallback}>
                            {title.charAt(0).toUpperCase()}
                          </span>
                        )}
                      </span>

                      <span className={s.courseMainInfo}>
                        <span className={s.courseTitle}>{title}</span>
                        <span className={s.courseMeta}>
                          {[course.language, course.learningMode]
                            .filter(Boolean)
                            .join(" / ") || "Course"}
                        </span>
                      </span>
                    </span>

                    {description && (
                      <span className={s.courseDescription}>{description}</span>
                    )}

                    <span className={s.courseDetailsGrid}>
                      {author && (
                        <span className={s.courseDetail}>
                          <span className={s.label}>Author</span>
                          <span className={s.value}>{author}</span>
                        </span>
                      )}
                      {levels && (
                        <span className={s.courseDetail}>
                          <span className={s.label}>Level</span>
                          <span className={s.value}>{levels}</span>
                        </span>
                      )}
                      {structureText && (
                        <span className={s.courseDetail}>
                          <span className={s.label}>Structure</span>
                          <span className={s.value}>{structureText}</span>
                        </span>
                      )}
                      {price && (
                        <span className={s.courseDetail}>
                          <span className={s.label}>Price</span>
                          <span className={s.value}>{price}</span>
                        </span>
                      )}
                    </span>

                    {tags && <span className={s.courseTags}>{tags}</span>}

                    <span className={s.courseAction}>
                      {selected ? "Selected" : "Add Course"}
                    </span>
                  </button>
                );
              })}
            </div>

            {selectedCourses.length > 0 && (
              <div className={s.selectedCoursesBlock}>
                <h3 className={s.groupTitle}>Selected Courses</h3>
                <div className={s.selectedCourseList}>
                  {selectedCourses.map((course) => (
                    <span key={course._id} className={s.selectedCoursePill}>
                      {getCourseTitle(course)}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </section>

      {projectId && (
        <section className={s.section}>
          <div>
            <h2 className={s.sectionTitle}>Courses in this Project</h2>
            <p className={s.sectionText}>
              Remove cloned Courses that already belong to this Project.
            </p>
          </div>

          {projectCoursesLoading && (
            <div className={s.emptyState}>Loading Project Courses...</div>
          )}

          {projectCoursesErrorMessage && (
            <div className={s.formAlert} role="alert">
              {projectCoursesErrorMessage}
            </div>
          )}

          {!projectCoursesLoading &&
            !projectCoursesErrorMessage &&
            projectCourses.length === 0 && (
              <div className={s.emptyState}>
                No Courses have been added to this Project yet.
              </div>
            )}

          {!projectCoursesLoading &&
            !projectCoursesErrorMessage &&
            projectCourses.length > 0 && (
              <div className={s.courseGrid}>
                {projectCourses.map((course) => {
                  const title = getCourseTitle(course);
                  const description = getCourseDescription(course);
                  const author = getCourseAuthor(course);
                  const structureText = getCourseStructureText(course);
                  const levels = course.levels?.join(", ");
                  const tags = course.tags?.map((tag) => tag.title).join(", ");
                  const imageUrl = courseImages[course._id];
                  const price = getCoursePrice(course);
                  const deleting = deletingCourseIds.includes(course._id);

                  return (
                    <article key={course._id} className={s.courseCard}>
                      <span className={s.courseCardHeader}>
                        <span className={s.courseImageBlock}>
                          {imageUrl ? (
                            <img
                              className={s.courseImage}
                              src={imageUrl}
                              alt={`${title} logo`}
                            />
                          ) : (
                            <span className={s.courseImageFallback}>
                              {title.charAt(0).toUpperCase()}
                            </span>
                          )}
                        </span>

                        <span className={s.courseMainInfo}>
                          <span className={s.courseTitle}>{title}</span>
                          <span className={s.courseMeta}>
                            {[course.language, course.learningMode]
                              .filter(Boolean)
                              .join(" / ") || "Course"}
                          </span>
                        </span>
                      </span>

                      {description && (
                        <span className={s.courseDescription}>
                          {description}
                        </span>
                      )}

                      <span className={s.courseDetailsGrid}>
                        {author && (
                          <span className={s.courseDetail}>
                            <span className={s.label}>Author</span>
                            <span className={s.value}>{author}</span>
                          </span>
                        )}
                        {levels && (
                          <span className={s.courseDetail}>
                            <span className={s.label}>Level</span>
                            <span className={s.value}>{levels}</span>
                          </span>
                        )}
                        {structureText && (
                          <span className={s.courseDetail}>
                            <span className={s.label}>Structure</span>
                            <span className={s.value}>{structureText}</span>
                          </span>
                        )}
                        {price && (
                          <span className={s.courseDetail}>
                            <span className={s.label}>Price</span>
                            <span className={s.value}>{price}</span>
                          </span>
                        )}
                      </span>

                      {tags && <span className={s.courseTags}>{tags}</span>}

                      <button
                        type="button"
                        className={s.deleteCourseButton}
                        onClick={() => deleteProjectCourse(course._id)}
                        disabled={deleting}
                      >
                        {deleting ? "Deleting..." : "Delete from Project"}
                      </button>
                    </article>
                  );
                })}
              </div>
            )}
        </section>
      )}
    </div>
  );
};

export default CoursesStep;
